import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { BRAND_NAME, ENDORSEMENT } from "@/lib/brand";
import { HOME } from "@/lib/homeContent";
import { useHowWeHelpContent } from "@/components/sections/HowWeHelp";
import { openCookiePreferences } from "@/lib/consent";
import { getAttribution } from "@/lib/attribution";
import { buildCommercialConsent } from "@/lib/commercialConsent";
import { metaLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPLORE = [
  { label: "How We Help", to: "/#how-we-help" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "About", to: "/about" },
  { label: "Resources", to: "/resources" },
  { label: "Free Guide", to: "/#guide" },
];

export default function Footer() {
  const { t, section } = useSiteContent();
  const wl = section("waitlist", HOME.waitlist);
  const { steps } = useHowWeHelpContent();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const leadFired = useRef(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Anchor links: scroll to the section top (works on the landing page and from other pages).
  const onExploreClick = (e, to) => {
    const [path, id] = to.split("#");
    if (!id) return;
    e.preventDefault();
    if (pathname === (path || "/")) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${id}`);
    } else {
      navigate(`${path || "/"}#${id}`);
    }
  };

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim(), attribution: getAttribution(), commercial_consent: buildCommercialConsent({}) });
      if (!leadFired.current) { leadFired.current = true; metaLead("newsletter"); }
      toast.success("Subscribed. Welcome aboard.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not subscribe. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#451C25] text-white pt-20 pb-10 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 border-b border-white/15 pb-16">
          <div className="md:col-span-6">
            <div data-testid="footer-brand">
              <BrandLogo variant="tagline" size="md" color="#F5EFE7" markVariant="ivory" subColor="#B3A17D" />
            </div>
            <p className="mt-6 text-white/65 text-[15px] leading-relaxed font-light max-w-md">
              {t("footer", "tagline", "Support for Americans planning and coordinating a move to Spain, from the first decisions to getting settled.")}
            </p>
            <form data-testid="newsletter-form" onSubmit={onSubscribe} className="mt-8 max-w-md flex items-center gap-3 border-b border-white/20 focus-within:border-[#B3A17D] transition-colors">
              <input
                data-testid="newsletter-input"
                required
                type="email"
                placeholder={t("footer", "newsletter_placeholder", "Your email for occasional updates")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent py-3 text-white placeholder:text-white/40 focus:outline-none"
              />
              <button data-testid="newsletter-submit" type="submit" disabled={submitting} className="text-[#B3A17D] hover:text-white transition-colors disabled:opacity-50" aria-label="Subscribe">
                <ArrowRight size={18} />
              </button>
            </form>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-white/45 mb-5">{t("footer", "services_label", "How we help")}</p>
            <ul className="space-y-1.5 text-white/80 text-[14px]">
              {steps.map((a, i) => (
                <li key={a.id || i}>
                  <Link to={`/#how-we-help-step-${i + 1}`} data-testid={`footer-help-step-${i + 1}`} className="inline-block py-1 hover:text-[#B3A17D] transition-colors">{a.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-white/45 mb-5">Explore</p>
            <ul className="space-y-1.5 text-white/80 text-[14px]">
              {EXPLORE.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={(e) => onExploreClick(e, l.to)} className="inline-block py-1 hover:text-[#B3A17D] transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p data-testid="footer-waitlist-line" className="pt-8 text-[13px] text-white/50">
          {wl.footer_line}{" "}
          <Link to="/resources#waitlist" className="inline-block py-1 text-white/70 hover:text-[#B3A17D] underline underline-offset-4 decoration-white/30 transition-colors">
            {wl.footer_cta}
          </Link>
        </p>
        <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-white/45">
          <div data-testid="footer-legal">© {new Date().getFullYear()} {BRAND_NAME} · {ENDORSEMENT}</div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-privacy" className="inline-block py-2 hover:text-white">{t("footer", "privacy_label", "Privacy")}</Link>
            <Link to="/terms" data-testid="footer-terms" className="inline-block py-2 hover:text-white">{t("footer", "terms_label", "Terms")}</Link>
            <button type="button" data-testid="footer-cookie-preferences" onClick={openCookiePreferences} className="inline-block py-2 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]">Cookie preferences</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
